import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SaasCentresAndStaff = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [activeTab, setActiveTab] = useState('centres'); // 'centres' | 'staff'
  const [centres, setCentres] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Add Centre Modal
  const [showCentreModal, setShowCentreModal] = useState(false);
  const [centreForm, setCentreForm] = useState({
    centreCode: '',
    centreName: '',
    address: '',
    phone: '',
    email: '',
    isActive: true
  });
  const [submittingCentre, setSubmittingCentre] = useState(false);

  // Add Staff Modal
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({
    employeeCode: '',
    fullName: '',
    designation: 'Consultant Pathologist',
    department: 'Pathology',
    centreId: '',
    canApproveReports: true,
    email: ''
  });
  const [submittingStaff, setSubmittingStaff] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      try {
        const res = await saasPlatformService.getTenants();
        const list = res.data || res || [];
        setTenants(list);
        if (list.length > 0) {
          const defaultTenant = list.find(t => t.tenantId === 9111) || list[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load tenants', err);
        setError('Failed to load tenants list.');
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, []);

  const loadData = async (tenantId) => {
    if (!tenantId) return;
    setDataLoading(true);
    setError(null);
    try {
      const [centresRes, staffRes] = await Promise.all([
        saasPlatformService.getTenantCentres(tenantId).catch(() => []),
        saasPlatformService.getTenantStaff(tenantId).catch(() => [])
      ]);
      setCentres(centresRes.data || centresRes || []);
      setStaff(staffRes.data || staffRes || []);
    } catch (err) {
      console.error('Failed to load centres and staff', err);
      setError('Failed to query tenant centres or clinical staff.');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTenantId) {
      loadData(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleCreateCentre = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSubmittingCentre(true);
    setError(null);
    try {
      await saasPlatformService.createTenantCentre(selectedTenantId, centreForm);
      setSuccess(`Centre ${centreForm.centreName} created successfully in tenant database!`);
      setShowCentreModal(false);
      setCentreForm({ centreCode: '', centreName: '', address: '', phone: '', email: '', isActive: true });
      loadData(selectedTenantId);
    } catch (err) {
      console.error('Failed to create centre', err);
      setError(err?.response?.data?.message || 'Failed to create centre.');
    } finally {
      setSubmittingCentre(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSubmittingStaff(true);
    setError(null);
    try {
      const payload = {
        ...staffForm,
        centreId: staffForm.centreId ? parseInt(staffForm.centreId, 10) : null
      };
      await saasPlatformService.createTenantStaff(selectedTenantId, payload);
      setSuccess(`Doctor/Staff ${staffForm.fullName} registered successfully with approval permissions!`);
      setShowStaffModal(false);
      setStaffForm({ employeeCode: '', fullName: '', designation: 'Consultant Pathologist', department: 'Pathology', centreId: '', canApproveReports: true, email: '' });
      loadData(selectedTenantId);
    } catch (err) {
      console.error('Failed to create staff', err);
      setError(err?.response?.data?.message || 'Failed to register clinical staff.');
    } finally {
      setSubmittingStaff(false);
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🏥</span> Tenant Centres, Doctors &amp; Clinical Staff
          </h1>
          <p>Multi-Branch Diagnostics, Facility Governance &amp; Pathologist Verification Rights</p>
        </div>
        <div className="saas-header-actions">
          {activeTab === 'centres' ? (
            <button className="saas-btn saas-btn-primary" onClick={() => setShowCentreModal(true)}>
              ➕ Add Facility Centre
            </button>
          ) : (
            <button className="saas-btn saas-btn-primary" onClick={() => setShowStaffModal(true)}>
              👨‍⚕️ Add Doctor / Staff
            </button>
          )}
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div><strong>Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box">
          <div><strong>Confirmation:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Tenant Selector & Sub-Navigation */}
      <div className="saas-card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Select Client:</label>
            <select
              className="saas-select"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              style={{ minWidth: '320px', fontWeight: 600 }}
            >
              {tenants.map(t => (
                <option key={t.tenantId} value={t.tenantId}>
                  #{t.tenantId} — {t.tenantCode} ({t.organizationName}) [{t.databaseName}]
                </option>
              ))}
            </select>
          </div>
          <div className="saas-nav-tabs" style={{ margin: 0, border: 'none' }}>
            <button
              className={`saas-nav-tab ${activeTab === 'centres' ? 'active' : ''}`}
              onClick={() => setActiveTab('centres')}
            >
              🏢 Facility Centres &amp; Branches ({centres.length})
            </button>
            <button
              className={`saas-nav-tab ${activeTab === 'staff' ? 'active' : ''}`}
              onClick={() => setActiveTab('staff')}
            >
              👨‍⚕️ Doctors &amp; Clinical Staff ({staff.length})
            </button>
          </div>
        </div>
      </div>

      {dataLoading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          ⏳ Querying {currentTenantObj?.databaseName}...
        </div>
      ) : activeTab === 'centres' ? (
        /* Centres Table */
        <div className="saas-card">
          <div className="saas-card-header">
            <h3>Registered Facility Centres ({centres.length})</h3>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Isolated in tenant schema <strong>{currentTenantObj?.databaseName}</strong>
            </span>
          </div>
          <div className="saas-table-container">
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Centre Code</th>
                  <th>Centre Name</th>
                  <th>Address &amp; Location</th>
                  <th>Contact Info</th>
                  <th>Status</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {centres.length > 0 ? (
                  centres.map((c, idx) => (
                    <tr key={c.centreId || idx}>
                      <td>
                        <strong style={{ color: '#0284c7' }}>{c.centreCode}</strong>
                      </td>
                      <td>
                        <strong>{c.centreName}</strong>
                      </td>
                      <td style={{ color: '#475569' }}>{c.address || '—'}</td>
                      <td>
                        <div>{c.phone || '—'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{c.email}</div>
                      </td>
                      <td>
                        <span className={`saas-badge ${c.isActive ? 'saas-badge-active' : 'saas-badge-suspended'}`}>
                          ● {c.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td style={{ color: '#64748b', fontSize: '0.8125rem' }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>
                      No facility centres registered. Click "Add Facility Centre" to register the primary branch.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Staff & Doctors Table */
        <div className="saas-card">
          <div className="saas-card-header">
            <h3>Doctors &amp; Clinical Staff ({staff.length})</h3>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Clinical verification privileges enforced server-side
            </span>
          </div>
          <div className="saas-table-container">
            <table className="saas-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Full Name</th>
                  <th>Designation / Dept</th>
                  <th>Centre</th>
                  <th>Sign-off Authority</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {staff.length > 0 ? (
                  staff.map((s, idx) => (
                    <tr key={s.employeeId || idx}>
                      <td style={{ fontFamily: 'monospace' }}>
                        <strong>{s.employeeCode}</strong>
                      </td>
                      <td>
                        <strong>{s.fullName}</strong>
                        {s.email && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{s.email}</div>}
                      </td>
                      <td>
                        <div>{s.designation}</div>
                        <div style={{ fontSize: '0.75rem', color: '#0284c7' }}>{s.department}</div>
                      </td>
                      <td>{s.centreName || (s.centreId ? `Centre #${s.centreId}` : 'All Centres')}</td>
                      <td>
                        {s.canApproveReports ? (
                          <span className="saas-badge saas-badge-active">
                            ✅ Authorized Pathologist
                          </span>
                        ) : (
                          <span className="saas-badge saas-badge-info">
                            Technician / Staff
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`saas-badge ${s.isActive ? 'saas-badge-active' : 'saas-badge-suspended'}`}>
                          ● {s.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8' }}>
                      No staff registered in tenant database. Click "Add Doctor / Staff" to register a clinical director.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Centre Modal */}
      {showCentreModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowCentreModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="saas-modal-header">
              <h3>Register Facility Centre: #{selectedTenantId}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowCentreModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateCentre}>
              <div className="saas-modal-body">
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Centre Code</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    placeholder="e.g. MAIN_LAB or BR_NORTH"
                    value={centreForm.centreCode}
                    onChange={(e) => setCentreForm({ ...centreForm, centreCode: e.target.value.toUpperCase() })}
                    required
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Centre Name</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    placeholder="e.g. Central Diagnostic Center"
                    value={centreForm.centreName}
                    onChange={(e) => setCentreForm({ ...centreForm, centreName: e.target.value })}
                    required
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Address</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    placeholder="Street, City, Pin"
                    value={centreForm.address}
                    onChange={(e) => setCentreForm({ ...centreForm, address: e.target.value })}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Phone</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      value={centreForm.phone}
                      onChange={(e) => setCentreForm({ ...centreForm, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Email</label>
                    <input
                      type="email"
                      className="saas-form-input"
                      value={centreForm.email}
                      onChange={(e) => setCentreForm({ ...centreForm, email: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowCentreModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-primary" disabled={submittingCentre}>
                  {submittingCentre ? 'Registering...' : '💾 Register Centre'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Staff Modal */}
      {showStaffModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowStaffModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="saas-modal-header">
              <h3>Register Clinical Staff / Doctor: #{selectedTenantId}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowStaffModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateStaff}>
              <div className="saas-modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Employee Code</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      placeholder="e.g. DOC-101"
                      value={staffForm.employeeCode}
                      onChange={(e) => setStaffForm({ ...staffForm, employeeCode: e.target.value.toUpperCase() })}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Full Name</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      placeholder="e.g. Dr. Ramesh Kumar"
                      value={staffForm.fullName}
                      onChange={(e) => setStaffForm({ ...staffForm, fullName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Designation</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      value={staffForm.designation}
                      onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Department</label>
                    <select
                      className="saas-form-input"
                      value={staffForm.department}
                      onChange={(e) => setStaffForm({ ...staffForm, department: e.target.value })}
                    >
                      <option value="Pathology">Pathology</option>
                      <option value="Radiology">Radiology</option>
                      <option value="Microbiology">Microbiology</option>
                      <option value="Biochemistry">Biochemistry</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Email Address</label>
                  <input
                    type="email"
                    className="saas-form-input"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={staffForm.canApproveReports}
                      onChange={(e) => setStaffForm({ ...staffForm, canApproveReports: e.target.checked })}
                    />
                    Grant Clinical Report Sign-Off &amp; Pathologist Verification Rights
                  </label>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '0.25rem' }}>
                    Enables diagnostic finalization authority in the clinical results workbench.
                  </span>
                </div>
              </div>
              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowStaffModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-primary" disabled={submittingStaff}>
                  {submittingStaff ? 'Registering...' : '👨‍⚕️ Register Staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasCentresAndStaff;
